/**
 * The board and everything around it, for whichever side is looking.
 *
 * Everything floats *on* the canvas: the rail on the left, the inspector on
 * the right when something is picked, the relationship bar at the foot when
 * two things are. The board itself fills the space it is given and never grows
 * the page — a genogram is explored by dragging it, not by scrolling past it.
 *
 * Shared by the therapist's page, the patient's page and the panel inside a
 * meeting, so the three cannot drift into three slightly different genograms.
 * The only prop that changes what a person can do is `editable`, and it hides
 * editing — never a symbol.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    AlertTriangle,
    Check,
    HelpCircle,
    Loader2,
    Redo2,
    Undo2,
    Users,
    WifiOff,
    X,
} from "lucide-react";

import { unionBehind } from "../../domain/genogramRules";
import { PRESENCE_INKS, assignInks } from "../../domain/presenceInk";
import { freeSpotNear, type Point } from "../../domain/layout";
import {
    removeHousehold,
    removeLink,
    removeNode,
    upsertHousehold,
    upsertLink,
    upsertNode,
    type Operation,
} from "../../domain/operations";
import {
    CENTRE_NODE_ID,
    CENTRE_RADIUS_STEP,
    DEFAULT_CENTRE_RADIUS,
    MAX_CENTRE_RADIUS,
    MAX_HOUSEHOLD_MEMBERS,
    MAX_SYSTEM_RADIUS,
    MIN_CENTRE_RADIUS,
    MIN_SYSTEM_RADIUS,
    type ChildType,
    type EmotionalType,
    type EnergyDirection,
    type LinkStrength,
    type MapLink,
    type MapNode,
    type MapState,
    type PersonGender,
    type SystemCategory,
    type UnionType,
    isPerson,
} from "../../domain/types";
import type { BoardPresence } from "../../application/useBoardPresence";
import type { SaveStatus } from "../../application/useSystemicMap";
import Inspector from "./Inspector";
import LinkPalette from "./LinkPalette";
import MapCanvas, { type Viewer } from "./MapCanvas";
import MapLegend from "./MapLegend";
import NodePalette from "./NodePalette";

interface Props {
    state: MapState;
    saveStatus: SaveStatus;
    onSubmit: (operations: Operation[]) => void;
    /** Taking back your own last action. Absent where there is no history. */
    onUndo?: () => void;
    onRedo?: () => void;
    canUndo?: boolean;
    canRedo?: boolean;
    /** The live channel, when there is one: pointers and who is present. */
    presence?: BoardPresence;
    /** Who is looking, so the board can mark the other person's edits. */
    viewer?: Viewer;
    /** Off inside a read-only view. Board editing, not map management. */
    editable?: boolean;
}

const newId = () =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `el-${Math.random().toString(36).slice(2, 10)}`;

export default function MapBoard({
    state,
    saveStatus,
    onSubmit,
    onUndo,
    onRedo,
    canUndo = false,
    canRedo = false,
    presence,
    viewer = { type: "therapist" },
    editable = true,
}: Props) {
    const { t } = useTranslation();
    // Two selections, not one: the first is what the inspector edits, and the
    // pair is what the relationship bar connects.
    const [selected, setSelected] = useState<string | null>(null);
    const [linking, setLinking] = useState<string | null>(null);
    const [legendOpen, setLegendOpen] = useState(false);
    // Where the board is currently looking. A new figure lands here rather
    // than at the board's origin, which may be nowhere near the screen.
    const viewCentre = useRef<Point>({ x: 0, y: 0 });
    const rememberCentre = useCallback((centre: Point) => {
        viewCentre.current = centre;
    }, []);

    // Ctrl/Cmd+Z and its redo twin, because a board is drawn with both hands
    // on the mouse and nobody reaches for a toolbar to take back a stray drag.
    useEffect(() => {
        if (!editable || !onUndo) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "z") return;
            const target = event.target as HTMLElement | null;
            // Undo inside a name field belongs to the field, not the board.
            if (target?.closest("input, textarea, select, [contenteditable]")) return;
            event.preventDefault();
            if (event.shiftKey) onRedo?.();
            else onUndo();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [editable, onUndo, onRedo]);

    /** The same assignment the pointers use, so the two agree. */
    const memberInks = useMemo(
        () => assignInks((presence?.members ?? []).map((member) => member.id)),
        [presence?.members],
    );

    const nodesById = useMemo(
        () => new Map(state.nodes.map((node) => [node.id, node])),
        [state.nodes],
    );
    const linksById = useMemo(
        () => new Map(state.links.map((link) => [link.id, link])),
        [state.links],
    );

    /** The home someone lives in, if any. At most one: you live somewhere. */
    const householdOf = useCallback(
        (personId: string) =>
            state.households.find((home) => home.members.includes(personId)) ?? null,
        [state.households],
    );

    const selectedNode = selected ? nodesById.get(selected) ?? null : null;
    const selectedLink = selected ? linksById.get(selected) ?? null : null;
    const first = selectedNode ?? selectedLink ?? null;
    const second = linking
        ? nodesById.get(linking) ?? linksById.get(linking) ?? null
        : null;

    const select = (id: string | null) => {
        if (id === null) {
            setSelected(null);
            setLinking(null);
            return;
        }
        if (selected && id !== selected) {
            // A second pick is the other end of a relationship, not a change
            // of mind: it is how a link gets drawn without a modal.
            setLinking(id);
            return;
        }
        setSelected(id);
        setLinking(null);
    };

    const addPerson = (gender: PersonGender) => {
        const person: MapNode = {
            id: newId(),
            type: "person",
            gender,
            name: "",
            birthYear: null,
            deathYear: null,
            deceased: false,
            index: false,
            notes: "",
            ...freeSpotNear(viewCentre.current, state.nodes),
        };
        onSubmit([upsertNode(person)]);
        setSelected(person.id);
        setLinking(null);
    };

    const addSystem = (system: SystemCategory) => {
        const node: MapNode = {
            id: newId(),
            type: "system",
            system,
            label: system === "custom" ? t("systemicMap.system.custom") : "",
            ...freeSpotNear(viewCentre.current, state.nodes),
        };
        onSubmit([upsertNode(node)]);
        setSelected(node.id);
        setLinking(null);
    };

    /**
     * Grows or shrinks the ring the household sits in.
     *
     * Sent as an ordinary edit, so the other side sees the circle move rather
     * than each client keeping its own idea of how big the family is.
     */
    const resizeCentre = (step: 1 | -1) => {
        const centre = state.nodes.find((node) => node.id === CENTRE_NODE_ID);
        if (!centre || centre.type !== "centre") return;
        const radius = Math.min(
            MAX_CENTRE_RADIUS,
            Math.max(
                MIN_CENTRE_RADIUS,
                (centre.radius ?? DEFAULT_CENTRE_RADIUS) + step * CENTRE_RADIUS_STEP,
            ),
        );
        if (radius === (centre.radius ?? DEFAULT_CENTRE_RADIUS)) return;
        onSubmit([upsertNode({ ...centre, radius })]);
    };

    /** Pins a note to the board, ready to be typed into. */
    const addNote = () => {
        const at = freeSpotNear(viewCentre.current, state.nodes);
        const note: MapNode = {
            id: newId(),
            type: "note",
            text: "",
            x: at.x,
            y: at.y,
        };
        onSubmit([upsertNode(note)]);
        // Selected straight away: a note is placed in order to write in it,
        // and the panel is where it is written.
        setSelected(note.id);
        setLinking(null);
    };

    /**
     * Puts the two picked people under one roof.
     *
     * A household is not a pair: grandparents, a lodger and three children are
     * one home. So picking someone who already lives somewhere adds the other
     * to *that* home rather than starting a second one, and repeating the
     * gesture grows the household one person at a time.
     */
    const addHousehold = () => {
        const picked = [first, second]
            .filter((element): element is MapNode => Boolean(element && isPerson(element as MapNode)))
            .map((element) => element.id);
        if (picked.length < 2) return;

        const homes = picked.map((id) => householdOf(id));
        const [homeOfFirst, homeOfSecond] = homes;

        if (homeOfFirst && homeOfSecond && homeOfFirst.id === homeOfSecond.id) {
            setLinking(null);
            return;
        }

        const home = homeOfFirst ?? homeOfSecond;
        if (!home) {
            onSubmit([upsertHousehold({ id: newId(), label: "", members: picked })]);
            setLinking(null);
            return;
        }

        const operations: Operation[] = [];
        // Someone can only live in one place at a time, so joining a home
        // means leaving the other one — and a home nobody is left living in
        // is not a household.
        const leaving = homeOfFirst && homeOfSecond ? homeOfSecond : null;
        if (leaving) {
            const rest = leaving.members.filter((id) => !picked.includes(id));
            operations.push(
                rest.length >= 2
                    ? upsertHousehold({ ...leaving, members: rest })
                    : removeHousehold(leaving.id),
            );
        }

        const members = [...new Set([...home.members, ...picked])];
        if (members.length > MAX_HOUSEHOLD_MEMBERS) return;
        operations.push(upsertHousehold({ ...home, members }));

        onSubmit(operations);
        setLinking(null);
    };

    /** Leave a home. The home goes too once fewer than two people are in it. */
    const leaveHousehold = (personId: string) => {
        const home = householdOf(personId);
        if (!home) return;
        const rest = home.members.filter((id) => id !== personId);
        onSubmit([
            rest.length >= 2
                ? upsertHousehold({ ...home, members: rest })
                : removeHousehold(home.id),
        ]);
    };

    const connect = (link: MapLink) => {
        onSubmit([upsertLink(link)]);
        setLinking(null);
    };

    const createUnion = (union: UnionType) => {
        if (!first || !second) return;
        connect({
            id: newId(),
            type: "union",
            from: first.id,
            to: second.id,
            union,
            since: null,
            until: null,
        });
    };

    const createChild = (child: ChildType) => {
        if (!first || !second) return;
        // The couple the child hangs from: one of the two picks, or the single
        // union the parent among them belongs to.
        const union = unionBehind(first, second, state.links);
        if (!union) return;
        const person = [first, second].find(
            (element) => element.id !== union.id && isPerson(element as MapNode),
        );
        if (!person) return;
        connect({ id: newId(), type: "child", from: union.id, to: person.id, child });
    };

    const createEmotional = (emotional: EmotionalType) => {
        if (!first || !second) return;
        connect({
            id: newId(),
            type: "emotional",
            from: first.id,
            to: second.id,
            emotional,
        });
    };

    const createSystemTie = (strength: LinkStrength, energy: EnergyDirection) => {
        if (!first || !second) return;
        const target = first.id === CENTRE_NODE_ID ? second : first;
        connect({
            id: newId(),
            type: "system",
            from: CENTRE_NODE_ID,
            to: target.id,
            strength,
            energy,
        });
    };

    const changeNode = (node: MapNode) => {
        // Marking a second index patient moves the mark rather than adding a
        // second one, matching what the server will do — so the board does not
        // show two of them for the half-second before the answer lands.
        const operations: Operation[] = [upsertNode(node)];
        if (isPerson(node) && node.index) {
            state.nodes
                .filter((other) => isPerson(other) && other.index && other.id !== node.id)
                .forEach((other) =>
                    operations.push(upsertNode({ ...other, index: false } as MapNode)),
                );
        }
        onSubmit(operations);
    };

    const removeSelected = () => {
        if (!selected) return;
        if (selected === CENTRE_NODE_ID) return;
        onSubmit([nodesById.has(selected) ? removeNode(selected) : removeLink(selected)]);
        setSelected(null);
        setLinking(null);
    };

    const bothArePeople =
        Boolean(first && second) &&
        isPerson(first as MapNode) &&
        isPerson(second as MapNode);

    return (
        <div className="relative h-full w-full">
            <MapCanvas
                state={state}
                selected={selected}
                linking={linking}
                viewer={viewer}
                onSelect={select}
                onMove={(id, x, y) => {
                    if (nodesById.has(id)) onSubmit([{ op: "move_node", payload: { id, x, y } }]);
                }}
                onResize={
                    editable
                        ? (node, radius) => {
                              const bounds =
                                  node.id === CENTRE_NODE_ID
                                      ? [MIN_CENTRE_RADIUS, MAX_CENTRE_RADIUS]
                                      : [MIN_SYSTEM_RADIUS, MAX_SYSTEM_RADIUS];
                              const kept = Math.min(
                                  bounds[1],
                                  Math.max(bounds[0], Math.round(radius)),
                              );
                              onSubmit([upsertNode({ ...node, radius: kept } as MapNode)]);
                          }
                        : undefined
                }
                onViewCentreChange={rememberCentre}
                cursors={presence?.cursors}
                inks={memberInks}
                onPointerAt={presence?.reportCursor}
                editable={editable}
            />

            {editable && onUndo && (
                <div className="absolute left-3 top-3 flex items-center gap-1 rounded-xl border border-border-subtle bg-surface-raised p-1 shadow-theme-md">
                    <HistoryButton
                        Icon={Undo2}
                        label={t("systemicMap.history.undo")}
                        onClick={onUndo}
                        disabled={!canUndo}
                    />
                    <HistoryButton
                        Icon={Redo2}
                        label={t("systemicMap.history.redo")}
                        onClick={() => onRedo?.()}
                        disabled={!canRedo}
                    />
                </div>
            )}

            {editable && (
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <NodePalette
                        kind={state.kind}
                        onAddPerson={addPerson}
                        onAddSystem={addSystem}
                        onAddHousehold={addHousehold}
                        onAddNote={addNote}
                        onResizeCentre={state.kind === "ecomap" ? resizeCentre : undefined}
                        householdEnabled={bothArePeople}
                        disabled={state.nodes.length >= 120}
                        disabledReasonKey="systemicMap.palette.full"
                    />
                </div>
            )}

            {editable && second && (
                // No `overflow` here: the bar's own menus open upward, and a
                // scroll container would clip them off at its top edge.
                <div className="absolute bottom-3 left-1/2 max-w-[calc(100%-8rem)] -translate-x-1/2">
                    <LinkPalette
                        kind={state.kind}
                        first={first}
                        second={second}
                        links={state.links}
                        onCreateUnion={createUnion}
                        onCreateChild={createChild}
                        onCreateEmotional={createEmotional}
                        onCreateSystem={createSystemTie}
                    />
                </div>
            )}

            {(selectedNode || selectedLink) && (
                <aside className="absolute right-3 top-3 max-h-[calc(100%-1.5rem)] w-72 overflow-y-auto rounded-xl border border-border-subtle bg-surface-raised p-4 shadow-theme-md">
                    <div className="mb-2 flex justify-end">
                        <button
                            type="button"
                            onClick={() => select(null)}
                            aria-label={t("common.close")}
                            className="flex h-9 w-9 items-center justify-center rounded-lg text-content-secondary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                            <X size={16} aria-hidden="true" />
                        </button>
                    </div>
                    <Inspector
                        node={selectedNode}
                        link={selectedLink}
                        onChangeNode={changeNode}
                        onChangeLink={(link) => onSubmit([upsertLink(link)])}
                        onRemove={removeSelected}
                        household={
                            selectedNode && isPerson(selectedNode)
                                ? householdOf(selectedNode.id)
                                : null
                        }
                        onChangeHousehold={(home) => onSubmit([upsertHousehold(home)])}
                        onLeaveHousehold={
                            selectedNode ? () => leaveHousehold(selectedNode.id) : undefined
                        }
                        editable={editable}
                    />
                </aside>
            )}

            <div className="absolute bottom-3 left-3 flex items-end gap-2">
                <button
                    type="button"
                    onClick={() => setLegendOpen((open) => !open)}
                    aria-expanded={legendOpen}
                    aria-label={t("systemicMap.legend.title")}
                    className="flex h-11 w-11 items-center justify-center rounded-xl border border-border-subtle bg-surface-raised text-content-secondary shadow-theme-md transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                    <HelpCircle size={18} aria-hidden="true" />
                </button>

                {legendOpen && (
                    <div className="max-h-[70vh] w-80 max-w-[calc(100vw-3rem)] overflow-y-auto rounded-xl border border-border-subtle bg-surface-overlay p-4 shadow-theme-lg">
                        <MapLegend kind={state.kind} />
                    </div>
                )}
            </div>

            {presence && presence.members.length > 1 && (
                <p
                    className="absolute right-3 top-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border-subtle bg-surface-raised px-2.5 py-1 text-xs font-medium text-content-secondary shadow-theme-md"
                    data-testid="board-presence"
                >
                    <Users size={14} aria-hidden="true" />
                    {presence.members.some((member) => member.name)
                        ? presence.members.map((member) => (
                              <span key={member.id} className="flex items-center gap-1">
                                  {/* The same colour as their pointer, so the
                                      list and the board agree on who is who. */}
                                  <span
                                      aria-hidden="true"
                                      className="h-2 w-2 rounded-full"
                                      style={{
                                          backgroundColor:
                                              memberInks[member.id] ?? PRESENCE_INKS[0],
                                      }}
                                  />
                                  {member.name || t("systemicMap.canvas.byPatient")}
                              </span>
                          ))
                        : t("systemicMap.presence.here", { count: presence.members.length })}
                </p>
            )}

            <SaveIndicator status={saveStatus} />
        </div>
    );
}

/** One step back or forward. Named, so the icon is never the only label. */
function HistoryButton({
    Icon,
    label,
    onClick,
    disabled,
}: {
    Icon: typeof Undo2;
    label: string;
    onClick: () => void;
    disabled: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            title={label}
            aria-label={label}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-content-secondary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:text-content-disabled disabled:hover:bg-transparent"
        >
            <Icon size={18} aria-hidden="true" />
        </button>
    );
}

/**
 * Whether the board is in step with the other side.
 *
 * Always a word beside the mark: a coloured dot alone says nothing to a screen
 * reader, and "did that save?" is exactly the question a therapist should not
 * have to guess at mid-session.
 */
function SaveIndicator({ status }: { status: SaveStatus }) {
    const { t } = useTranslation();
    if (status === "idle") return null;

    const view = {
        saving: { Icon: Loader2, key: "saving", tone: "text-content-secondary" },
        saved: { Icon: Check, key: "saved", tone: "text-status-success" },
        error: { Icon: WifiOff, key: "error", tone: "text-status-critical" },
        rejected: {
            Icon: AlertTriangle,
            key: "rejected",
            tone: "text-status-critical",
        },
    }[status];

    return (
        <p
            role="status"
            aria-live="polite"
            className={`absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-1.5 rounded-lg border border-border-subtle bg-surface-raised px-2.5 py-1 text-xs font-medium shadow-theme-md ${view.tone}`}
        >
            <view.Icon
                size={14}
                aria-hidden="true"
                className={status === "saving" ? "animate-spin motion-reduce:animate-none" : ""}
            />
            {t(`systemicMap.save.${view.key}`)}
        </p>
    );
}
